// Bind each person to their own staff-advance account.
//
// For an entity whose advance policy is "each employee's own account": when a
// claim was partly funded by an advance, the recovery credits THAT person's
// account rather than one shared control account.
//
// Two things about identity, because getting them wrong would be invisible:
//
//   the id is the AUTH USER id — what an expense report carries and what the
//   advance journal line resolves against. The payroll roster uses a
//   different id (employee-service's primary key), so binding from there
//   would store configuration keyed by an id nothing ever matches.
//
//   the list is everyone who has CLAIMED an expense for this company, not the
//   whole payroll. You only need to bind people who actually claim, and a
//   company with four claimants should not scroll past four hundred names.
//
// A name that the directory could not resolve shows as the id. Not invented,
// not hidden: the row is still bindable and nobody is shown a wrong name.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertTriangle, Loader2, Users } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getEmployeeAdvanceAccounts, setEmployeeAdvanceAccounts,
  type AccountingProvider, type EmployeeAdvanceAccountsView,
} from '@/services/accountingService';

interface Props {
  provider?: AccountingProvider;
  /** accounting:map — read-only without it. */
  canMap: boolean;
  systemName: string;
  legalEntityId: string;
}

/** changedOnly returns the bindings that actually differ from what is stored.
 *  Pure, and the reason the save is safe: sending every row would re-write
 *  bindings nobody touched, and one of them freeing an account could trip the
 *  one-account-one-person rule on a change the user did not make. */
export const changedOnly = (
  draft: Record<string, string>,
  stored: Record<string, string>,
): { employee_id: string; external_account_id: string }[] =>
  Object.keys(draft)
    .filter((id) => (draft[id] ?? '') !== (stored[id] ?? ''))
    .map((id) => ({ employee_id: id, external_account_id: draft[id] ?? '' }));

export const EmployeeAdvanceAccounts: React.FC<Props> = ({
  provider = 'quickbooks', canMap, systemName, legalEntityId,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<EmployeeAdvanceAccountsView | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [filter, setFilter] = useState('');

  const stored = useMemo(() => {
    const out: Record<string, string> = {};
    (view?.rows ?? []).forEach((r) => { out[r.employee_id] = r.external_account_id ?? ''; });
    return out;
  }, [view]);

  const load = useCallback(async () => {
    if (!legalEntityId) return;
    setLoading(true);
    try {
      const v = await getEmployeeAdvanceAccounts(provider, legalEntityId);
      setView(v);
      const next: Record<string, string> = {};
      v.rows.forEach((r) => { next[r.employee_id] = r.external_account_id ?? ''; });
      setDraft(next);
      setUnavailable(false);
    } catch {
      // A section of a settings screen, not the screen itself: it hides rather
      // than pushing an error at someone who came to map accounts.
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [provider, legalEntityId]);

  useEffect(() => { void load(); }, [load]);

  const changes = useMemo(() => changedOnly(draft, stored), [draft, stored]);

  const save = async () => {
    setSaving(true);
    try {
      const next = await setEmployeeAdvanceAccounts(provider, legalEntityId, changes);
      setView(next);
      const fresh: Record<string, string> = {};
      next.rows.forEach((r) => { fresh[r.employee_id] = r.external_account_id ?? ''; });
      setDraft(fresh);
      toast.success(t('accounting.advanceAccountsSaved', {
        defaultValue: 'Saved. New advance recoveries post to these accounts.',
      }));
    } catch (e) {
      // The server refuses by name — "Asha — Advances is already bound to
      // Juma" — and that sentence is more useful than ours.
      toast.error(getApiErrorMessage(e, t('accounting.advanceAccountsSaveFailed', {
        defaultValue: 'Could not save the advance accounts.',
      })));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('accounting.advanceAccountsLoading', { defaultValue: 'Reading advance accounts…' })}
      </CardContent></Card>
    );
  }
  if (unavailable || !view) return null;

  const label = (r: { employee_id: string; employee_name?: string }) =>
    r.employee_name?.trim() || r.employee_id;

  const needle = filter.trim().toLowerCase();
  const rows = needle
    ? view.rows.filter((r) => label(r).toLowerCase().includes(needle))
    : view.rows;
  const unbound = view.rows.filter((r) => !r.external_account_id).length;

  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <div className="flex items-start gap-3">
          <Users className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-foreground">
              {t('accounting.advanceAccountsTitle', { defaultValue: 'Staff advance accounts' })}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('accounting.advanceAccountsHelp', {
                defaultValue:
                  'This company recovers advances to each person’s own {{system}} account. Everyone who has claimed an expense here is listed; one account belongs to one person.',
                system: systemName,
              })}
            </p>
          </div>
        </div>

        {unbound > 0 && (
          // Said plainly, because an unbound person BLOCKS their next
          // advance-funded claim rather than falling back to a shared account.
          <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('accounting.advanceAccountsUnbound', {
              defaultValue:
                '{{count}} of these people have no account yet. An advance-funded claim of theirs cannot be posted until they do.',
              count: unbound,
            })}
          </p>
        )}

        {view.rows.length > 6 && (
          <input
            type="search"
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground sm:w-64"
            placeholder={t('accounting.advanceAccountsFilter', { defaultValue: 'Find a person…' })}
            aria-label={t('accounting.advanceAccountsFilter', { defaultValue: 'Find a person…' })}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        )}

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('accounting.person', { defaultValue: 'Person' })}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('accounting.advanceAccountIn', {
                    defaultValue: 'Advance account in {{system}}', system: systemName,
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // The row's own account stays selectable even though it is
                // "taken" — by this person. Candidates exclude every bound
                // account, so without this the select would show blank for
                // somebody already bound.
                const options = [...view.candidates];
                if (r.external_account_id && !options.some((a) => a.ID === r.external_account_id)) {
                  options.unshift({
                    ID: r.external_account_id,
                    Name: r.external_account_name || r.external_account_id,
                    FullyQualifiedName: '', AccountType: r.external_account_type || '',
                    AccountSubType: '', Classification: '', Currency: '', Active: true,
                  });
                }
                return (
                  <tr key={r.employee_id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <span className={r.employee_name ? '' : 'font-mono text-[11px] text-muted-foreground'}>
                        {label(r)}
                      </span>
                      {r.problem && (
                        <span className="ml-2 text-danger">
                          {r.problem === 'inactive'
                            ? t('accounting.accountInactive', { defaultValue: 'account is inactive' })
                            : t('accounting.accountUnknown', { defaultValue: 'account no longer exists' })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        aria-label={t('accounting.advanceAccountFor', {
                          defaultValue: 'Advance account for {{name}}', name: label(r),
                        })}
                        className="h-8 w-full min-w-[16rem] rounded-md border border-border bg-background px-2 text-xs text-foreground"
                        value={draft[r.employee_id] ?? ''}
                        disabled={!canMap || saving}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, [r.employee_id]: e.target.value }))}
                      >
                        <option value="">
                          {t('accounting.advanceAccountNone', { defaultValue: 'Not bound' })}
                        </option>
                        {options.map((a) => (
                          <option key={a.ID} value={a.ID}>{a.Name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-3 py-6 text-center text-muted-foreground">
                    {view.rows.length === 0
                      ? t('accounting.advanceAccountsNobody', {
                          defaultValue: 'Nobody has claimed an expense for this company yet.',
                        })
                      : t('accounting.advanceAccountsNoMatch', { defaultValue: 'Nobody matches that.' })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {canMap && (
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={() => void save()} disabled={saving || changes.length === 0}>
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
              {t('accounting.advanceAccountsSave', { defaultValue: 'Save advance accounts' })}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t('accounting.advanceAccountsAppliesNext', {
                defaultValue: 'Applies to recoveries posted from now on; nothing already posted changes.',
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeAdvanceAccounts;
